import { Typography, Row, Col, Drawer } from "antd";
import { useEffect, useRef, useState } from "react";
import { ClockCircleOutlined, CloseOutlined } from "@ant-design/icons";
// import { Button } from "@digitallabs/one-x-ui";
import { Button } from "../../resourses/digitallabs/one-x-ui";
import SuccessAnimation from "./SuccessAnimation";
import "../../styles/OtpVerificationModal.css";

const { Text } = Typography;

const OtpVerificationModal = ({
  visible,
  onCancel,
  onVerify,
  onResend,
  countdown = 120, //  2 minutes
  title = "Verify OTP",
  subtitle1 = "OTP sent on official E-mail ID & Mobile No.",
  subtitle2,
  otpLength = 6,
  renderAsModal = true,
}) => {
  const [otp, setOtp] = useState(Array(otpLength).fill(""));
  const [timer, setTimer] = useState(countdown);
  const [resendTrigger, setResendTrigger] = useState(false);
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const inputRefs = useRef([]);

  // Convert seconds → MM:SS
  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60).toString();
    const s = (seconds % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  // Reset OTP when otpLength changes
  useEffect(() => {
    setOtp(Array(otpLength).fill(""));
  }, [otpLength]);

  // Countdown logic — restarts on visible OR resendTrigger change
  useEffect(() => {
    if (!visible) {
      setTimer(countdown);
      return;
    }

    setTimer(countdown); // reset timer when modal opens or resend is clicked

    const interval = setInterval(() => {
      setTimer((prev) => {
        if (prev > 0) return prev - 1;
        clearInterval(interval);
        return 0;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [visible, resendTrigger, countdown]);

  // Autofocus first input
  useEffect(() => {
    if (visible) {
      setTimeout(() => {
        inputRefs.current[0]?.focus();
      }, 50);
    }
  }, [visible]);

  // Handle digit change
  const handleChange = (value, index) => {
    if (!/^\d?$/.test(value)) return;
    const updated = [...otp];
    updated[index] = value;
    setOtp(updated);

    if (value && index < otpLength - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  // Handle backspace + Enter
  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === "Enter" && isOtpComplete) {
      handleVerify();
    }
  };

  const handleVerify = () => {
    const finalOtp = otp.join("");
    if (finalOtp.length === otpLength) {
      onVerify(finalOtp);
    }
  };

  // Resend logic with proper reset
  const handleResend = () => {
    onResend();
    setOtp(Array(otpLength).fill(""));
    setResendTrigger((prev) => !prev); // 🔵 force restart countdown
    inputRefs.current[0]?.focus();
  };

  const isOtpComplete = otp.every((digit) => digit.trim() !== "");

  const OTPFields = () => {
    return (
      <div>
        <label style={{ fontSize: 14, fontWeight: "500", color: "#4B5563" }}>Enter OTP</label>

        <Row gutter={12}>
          {otp.map((digit, i) => (
            <Col key={i} span={24 / otpLength}>
              <input
                ref={(el) => (inputRefs.current[i] = el)}
                type="password"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(e.target.value, i)}
                onKeyDown={(e) => handleKeyDown(e, i)}
                style={{
                  width: "100%",
                  padding: "10px 0",
                  fontSize: 24,
                  textAlign: "center",
                  border: "none",
                  borderBottom: "2px solid #d9d9d9",
                  outline: "none",
                }}
              />
            </Col>
          ))}
        </Row>

        <Row style={{ marginTop: 16 }} align="middle">
          <ClockCircleOutlined style={{ fontSize: 16, marginRight: 8, color: "#888" }} />

          {timer > 0 ? (
            <Text type="secondary">Resend after {formatTime(timer)}</Text>
          ) : (
            <Button variant="link" onClick={handleResend}>
              Resend OTP
            </Button>
          )}
        </Row>

        <Button
          className="primary-button"
          onClick={handleVerify}
          disabled={!isOtpComplete}
          style={{ marginTop: 24, width: "100%", fontSize: "medium" }}
        >
          {renderAsModal === true ? "I confirm" : "Submit"}
        </Button>
      </div>
    );
  };

  return renderAsModal === true ? (
    <>
      <Drawer className="custom-drawer" open={visible} placement="bottom" onClose={onCancel} height={"70vh"} closable={false}>
        {showSuccessAnimation ? (
          <SuccessAnimation successMessage={"Verification completed successfully!"} />
        ) : (
          <>
            <Row justify="space-between" align="middle" className="custom-drawer-header">
              <label style={{ fontWeight: 600, fontSize: 28, color: "#1C2633" }}>{title}</label>
              <CloseOutlined onClick={onCancel} style={{ fontSize: 18, cursor: "pointer", color: "#475467" }} />
            </Row>

            <br />
            <div style={{ fontWeight: 300, color: "#475467", fontSize: 19 }}>{subtitle1}</div>
            <br />
            <div style={{ fontWeight: 400, color: "#475467", fontSize: 17 }}>{subtitle2}</div>
            <br />

            {OTPFields()}
          </>
        )}
      </Drawer>
    </>
  ) : (
    <div>{OTPFields()}</div>
  );
};

export default OtpVerificationModal;
